import { getInputEmail, getInputPassword } from "../content/login";
import * as axiosLib from "axios";
import {
  apiAudioTrack,
  apiAuthRoot,
  apiChart,
  apiChartsList,
  apiCollectionRoot,
  apiEpisode,
  apiMovie,
  apiPageObject,
  apiSearchResponse,
  apiSerial,
  apiSubtitleTrack,
} from "./api-models";
import { isLogin, isTokenValid, forceLogout, getAccessToken } from "../utils";
import { getRegionByDomain } from "../static-res";

// Функция для получения базового URL API
function getBaseApiURL(): string {
  return "https://api.screenify.shop/api";
}

const baseApiURL = getBaseApiURL();

// Функция для получения регионального пути
function getRegionalPath(): string {
  const region = getRegionByDomain();
  console.log(`Detected region: ${region} for domain: ${window.location.hostname}`);
  
  // Если регион mexico, используем /mexico/
  if (region === 'mexico') {
    return 'mexico';
  }
  
  // Если регион spain, используем /spain/
  if (region === 'spain') {
    return 'spain';
  }
  
  // Если регион france, используем /france/
  if (region === 'france') {
    return 'france';
  }
  
  // Если регион brazil, используем /brazil/
  if (region === 'brazil') {
    return 'brazil';
  }
  
  // По умолчанию global
  return 'global';
}

const authURL = "auth";
const loginURL = "login";

const pagesURL = "pages";
const collectionsURL = "collections";
const moviesURL = "movies";
const serialURL = "serials";
const episodesURL = "episodes";
const similarURL = "similar";
const audioTracksURL = "audio-tracks";
const subsTracksURL = "subtitle-tracks";
const searchURL = "search";
const chartsURL = "charts";
const studiosURL = "studios";

// Use a dedicated axios instance with a sane timeout to avoid very long hangs
const axios = axiosLib.default.create({ timeout: 10000 });

// Функция проверки аутентификации перед API запросами
function checkAuthentication(): boolean {
  if (!isLogin()) {
    console.log("User is not logged in");
    return false;
  }
  
  if (!isTokenValid()) {
    console.log("User token is invalid, forcing logout");
    forceLogout();
    return false;
  }
  
  return true;
}

// Добавляем interceptor для проверки аутентификации перед каждым запросом
axios.interceptors.request.use(
  (config) => {
    // Проверяем аутентификацию только для защищенных эндпоинтов
    // Исключаем публичные эндпоинты и логин
    const regionalPath = getRegionalPath();
    const isPublicEndpoint = config.url?.includes('/auth/') || 
                           config.url?.includes(`/${regionalPath}`) ||
                           config.url?.includes('/search') ||
                           config.url?.includes('/collections') ||
                           config.url?.includes('/pages') ||
                           config.url?.includes('/movies') ||
                           config.url?.includes('/serials') ||
                           config.url?.includes('/episodes') ||
                           config.url?.includes('/audio-tracks') ||
                           config.url?.includes('/subtitle-tracks') ||
                           config.url?.includes('/similar') ||
                           config.url?.includes('/studios') ||
                           config.url?.includes(`/charts/${regionalPath}`);
    
    if (!isPublicEndpoint && !checkAuthentication()) {
      // Если аутентификация не прошла, отменяем запрос
      return Promise.reject(new Error('Authentication failed'));
    }
    
    // Добавляем токен авторизации к запросам
    if (!isPublicEndpoint && isLogin()) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Добавляем interceptor для обработки ответов
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Если получили 401 Unauthorized, принудительно выходим из системы
    if (error.response && error.response.status === 401) {
      console.log("Received 401 Unauthorized, forcing logout");
      forceLogout();
    }
    return Promise.reject(error);
  }
);

async function getWithRetry<T>(url: string, attempts: number = 2): Promise<T> {
  let lastError: any = null;
  for (let tryIndex = 0; tryIndex <= attempts; tryIndex++) {
    try {
      const response = await axios.get(url);
      return response.data as T;
    } catch (error) {
      lastError = error;
      if (tryIndex < attempts) {
        await new Promise((r) => setTimeout(r, 400 * (tryIndex + 1)));
        continue;
      }
    }
  }
  console.error("Error:", lastError);
  return null as unknown as T;
}

async function postWithRetry<T>(url: string, data: any, attempts: number = 1): Promise<T> {
  let lastError: any = null;
  for (let tryIndex = 0; tryIndex <= attempts; tryIndex++) {
    try {
      const response = await axios.post(url, data);
      return response.data as T;
    } catch (error) {
      lastError = error;
      console.error(`Attempt ${tryIndex + 1} failed:`, error);
      if (tryIndex < attempts) {
        await new Promise((r) => setTimeout(r, 400 * (tryIndex + 1)));
        continue;
      }
    }
  }
  console.error("All attempts failed. Last error:", lastError);
  throw lastError; // Бросаем ошибку вместо возврата null
}

export async function apiGetPage(pageID: string): Promise<apiPageObject> {
  const url = `${baseApiURL}/${collectionsURL}/${getRegionalPath()}/${pageID}/content`;
  console.log(`API GetPage URL: ${url}`);
  
  try {
    const result = await getWithRetry<apiPageObject>(url);
    console.log(`API GetPage result:`, result);
    return result;
  } catch (error) {
    console.error(`API GetPage error for ${pageID}:`, error);
    throw error;
  }
}

export async function apiGetCharts(): Promise<apiChartsList> {
  return getWithRetry<apiChartsList>(`${baseApiURL}/${chartsURL}/${getRegionalPath()}`);
}

export async function apiGetCollection(
  slug: string,
  pageNumber: number
): Promise<apiCollectionRoot> {
  return getWithRetry<apiCollectionRoot>(
    `${baseApiURL}/${collectionsURL}/${slug}?page=${pageNumber}`
  );
}

export async function apiGetMovie(movieId: string): Promise<apiMovie> {
  return getWithRetry<apiMovie>(`${baseApiURL}/${moviesURL}/${movieId}`);
}
export async function apiGetSerial(serialId: string): Promise<apiSerial> {
  return getWithRetry<apiSerial>(`${baseApiURL}/${serialURL}/${serialId}`);
}
export async function apiGetSeasonEpisodes(
  serialId: string,
  seasonNumber: string
): Promise<apiEpisode[]> {
  return getWithRetry<apiEpisode[]>(
    `${baseApiURL}/${serialURL}/${serialId}/${seasonNumber}/${episodesURL}`
  );
}
export async function apiGetSubs(imdbid: string): Promise<apiSubtitleTrack[]> {
  return getWithRetry<apiSubtitleTrack[]>(
    `${baseApiURL}/${subsTracksURL}/${imdbid}`
  );
}
export async function apiGetAudio(imdbid: string): Promise<apiAudioTrack[]> {
  return getWithRetry<apiAudioTrack[]>(
    `${baseApiURL}/${audioTracksURL}/${imdbid}`
  );
}
export async function apiGetSimilarMovies(imdbid: string): Promise<apiMovie[]> {
  return getWithRetry<apiMovie[]>(
    `${baseApiURL}/${moviesURL}/${similarURL}/${imdbid}`
  );
}
export async function apiGetSimilarSerials(
  imdbid: string
): Promise<apiSerial[]> {
  return getWithRetry<apiSerial[]>(
    `${baseApiURL}/${serialURL}/${similarURL}/${imdbid}`
  );
}
export async function apiSearch(
  querry: string,
  page: string
): Promise<apiSearchResponse> {
  return getWithRetry<apiSearchResponse>(
    `${baseApiURL}/${searchURL}?searchStr=${querry}&page=${page}`
  );
}
export async function apiGetStudio(studioId: string): Promise<any> {
  return getWithRetry<any>(`${baseApiURL}/${studiosURL}/${studioId}`);
}

export async function apiGetMainStudios(): Promise<any> {
  return getWithRetry<any>(`${baseApiURL}/${studiosURL}/main/global`);
}

export async function apiAuthLogin(
  login: string,
  password: string
): Promise<apiAuthRoot | false> {
  // Валидация входных данных
  if (!login || !password || login.trim() === '' || password.trim() === '') {
    console.error("Login or password is empty");
    return false;
  }

  const data = {
    email: login.trim(),
    password: password.trim(),
  };
  
  try {
    console.log("Attempting login for:", data.email);
    const response = await postWithRetry<apiAuthRoot>(
      `${baseApiURL}/${authURL}/${loginURL}`,
      data,
      1
    );
    
    if (response && response.user && response.accessToken) {
      console.log("Login successful for:", response.user.email);
      return response;
    } else {
      console.error("Invalid response format:", response);
      return false;
    }
  } catch (error: any) {
    console.error("Login failed:", error);
    
    // Обработка различных типов ошибок
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.response.statusText;
      console.error(`HTTP ${status}: ${message}`);
      
      if (status === 401) {
        console.error("Invalid credentials");
      } else if (status === 429) {
        console.error("Too many requests");
      } else if (status >= 500) {
        console.error("Server error");
      }
    } else if (error.request) {
      console.error("Network error - no response received");
    } else {
      console.error("Request setup error:", error.message);
    }
    
    return false;
  }
}
